import { useQuery } from '@apollo/client/react';
import {
  STAFF_TROUBLESHOOT_SESSIONS,
  STAFF_TROUBLESHOOT_SESSION_TRANSCRIPT,
} from '@staff-app/graphql-client';

/** One cross-tenant troubleshoot session row (mirrors staff-api StaffTroubleshootSession). */
export interface TroubleshootSession {
  sessionId: string;
  accountId?: string | null;
  teamId?: string | null;
  deviceUUID: string;
  deviceId?: string | null;
  deviceName?: string | null;
  title?: string | null;
  actor: string;
  mode: string; // 'staff' | 'customer'
  status: string; // 'creating' | 'running' | 'paused' | 'ended' | 'failed'
  region?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  threadId?: string | null;
}

/** One recorded transcript event (mirrors staff-api StaffTroubleshootEvent). */
export interface TroubleshootEvent {
  seq: number;
  ts?: string | null;
  type: string;
  actor?: string | null;
  payloadJson?: string | null;
}

/** Cross-tenant session list; pass `deviceUuid` to scope to one device (the per-device drawer).
 *  `skip` lets a consumer that already has the session avoid firing the (cross-tenant) list query. */
export function useTroubleshootSessions(deviceUuid?: string, options?: { skip?: boolean }) {
  return useQuery<{ staffTroubleshootSessions: TroubleshootSession[] }, { deviceUuid?: string }>(
    STAFF_TROUBLESHOOT_SESSIONS,
    { variables: { deviceUuid }, fetchPolicy: 'cache-and-network', skip: options?.skip },
  );
}

/** Read-only transcript of one session (any tenant). */
export function useTroubleshootSessionTranscript(sessionId: string) {
  return useQuery<
    { staffTroubleshootSessionTranscript: TroubleshootEvent[] },
    { sessionId: string }
  >(STAFF_TROUBLESHOOT_SESSION_TRANSCRIPT, {
    variables: { sessionId },
    fetchPolicy: 'cache-and-network',
    skip: !sessionId,
  });
}
